import { IEstimateFormSnapshot } from "pages/CreateOrder/utils/estimateFormSnapshot";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";

/**
 * CLE-3043 — what the page the staff chat widget sits on can tell Clear Health AI about itself,
 * beyond its URL. Labels and ids only, never a field's value. Two hints:
 *
 * - `blockingIssues` — the create / review / copy / edit estimate pages publish why "Create and
 *   Send" is disabled (CreateEstimateBlockersPublisher), and StaffChatWidget sends it as
 *   `data.page.blockingIssues` with every message. `null` = the page publishes nothing (the
 *   default; a publisher clears it on unmount). `[]` = the page publishes, and nothing is blocking.
 * - `focusedOrderId` — the estimate the user has in focus on an Estimates list (OrdersTable: the
 *   row they expanded last, see pickFocusedOrderId). StaffChatWidget sends it as `data.orderId` on
 *   a page with no estimate of its own (withFocusedOrder), so "this patient" on the list means
 *   that row. `null` = no row in focus (the default; the table clears it on unmount).
 * - `form` — CLE-3043 follow-up: what the staff member has typed into the create / review / copy
 *   estimate form (buildEstimateFormSnapshot — the patient's name and date of birth, payer, member
 *   ID, date of service, procedures; never phone, email or benefits), published by the same
 *   CreateEstimateBlockersPublisher. StaffChatWidget sends it as `data.page.form`, so "this patient"
 *   on the New Estimate page means the form. It is the one hint that carries field VALUES. `null` =
 *   nothing typed yet, or not on those pages (the publisher clears it on unmount).
 */
interface IStaffChatPageHints {
  blockingIssues: string[] | null;
  setBlockingIssues: (issues: string[] | null) => void;
  focusedOrderId: string | null;
  setFocusedOrderId: (orderId: string | null) => void;
  form: IEstimateFormSnapshot | null;
  setForm: (form: IEstimateFormSnapshot | null) => void;
}

const StaffChatPageHintsContext = createContext<IStaffChatPageHints>({
  blockingIssues: null,
  setBlockingIssues: () => undefined,
  focusedOrderId: null,
  setFocusedOrderId: () => undefined,
  form: null,
  setForm: () => undefined,
});

export const useStaffChatPageHints = (): IStaffChatPageHints =>
  useContext(StaffChatPageHintsContext);

const sameIssues = (a: string[] | null, b: string[] | null): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.length === b.length &&
    a.every((issue, index) => issue === b[index]));

/** Snapshots are plain JSON built in a fixed key order (buildEstimateFormSnapshot), so this is structural equality. */
const sameForm = (
  a: IEstimateFormSnapshot | null,
  b: IEstimateFormSnapshot | null
): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

export function StaffChatPageHintsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [blockingIssues, setIssuesState] = useState<string[] | null>(null);
  // A plain string: setting the same id again is already a no-op re-render for React.
  const [focusedOrderId, setFocusedOrderId] = useState<string | null>(null);
  const [form, setFormState] = useState<IEstimateFormSnapshot | null>(null);

  // Equal lists keep the old reference, so a publisher re-sending the same list re-renders nothing.
  const setBlockingIssues = useCallback((issues: string[] | null) => {
    setIssuesState((current) =>
      sameIssues(current, issues) ? current : issues
    );
  }, []);

  // Same for the typed form: re-publishing an equal snapshot every second re-renders nothing.
  const setForm = useCallback((next: IEstimateFormSnapshot | null) => {
    setFormState((current) => (sameForm(current, next) ? current : next));
  }, []);

  const contextValue = useMemo(
    () => ({
      blockingIssues,
      setBlockingIssues,
      focusedOrderId,
      setFocusedOrderId,
      form,
      setForm,
    }),
    [blockingIssues, setBlockingIssues, focusedOrderId, form, setForm]
  );

  return (
    <StaffChatPageHintsContext.Provider value={contextValue}>
      {children}
    </StaffChatPageHintsContext.Provider>
  );
}
